import { compulsoryColumns, excludedColumns } from "@Components/FileColumnsTreeView/constants";
import axiosWrapper from "@AxiosWrapper";
import type { GetDistinctFilePropertyNamesResponse } from "@ServerTypes/queries/queries.schema";
import { getBackendUrl } from "@Utils";

/** A grid column referenced either by its plain field name or by a definition that carries one. */
export type ColumnRef = string | { field?: unknown };

/** Any non-null object, read as an open property bag. */
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/** A nested property bag: objects only, arrays excluded. */
const isPlainRecord = (value: unknown): value is Record<string, unknown> => isRecord(value) && !Array.isArray(value);

/**
 * SCHEMA GAP: `getDistinctFilePropertyNamesResponse` declares the names under `result`, but
 * queries.service returns them under an undeclared `data` key. Read whichever key is present and
 * keep only the string entries, so this survives both the current server and a corrected schema.
 */
const readDistinctPropertyNames = (payload: unknown): string[] => {
  if (!isRecord(payload)) return [];
  const names = "data" in payload ? payload.data : payload.result;
  return Array.isArray(names) ? names.filter((name): name is string => typeof name === "string") : [];
};

/**
 * Drops the excluded and compulsory columns. This copy is only fed the property-name list from
 * `getFilePropertyNames` below, so it is plain `string[]` in and out.
 */
export const getFilteredColumns = (columns: string[]): string[] =>
  columns.filter(
    (column) =>
      !excludedColumns.some((excludedColumn) => excludedColumn.field === column) &&
      !compulsoryColumns.some((compulsoryColumn) => compulsoryColumn.field === column),
  );

export const getFilePropertyNames = async (fileVersionId: string | string[]) => {
  const propertyNames = await axiosWrapper.post<GetDistinctFilePropertyNamesResponse>(
    `${getBackendUrl()}/queries/getDistinctFilePropertyNames`,
    {
      fileVersionId: Array.isArray(fileVersionId) ? fileVersionId : [fileVersionId],
    },
  );

  let columns: string[] = [];
  if (propertyNames.data.status) {
    columns = getFilteredColumns(readDistinctPropertyNames(propertyNames.data));
  }
  return [columns, propertyNames.data];
};

export const convertToDotNotation = (
  obj: Record<string, unknown>,
  parentKey = "",
  result: Record<string, unknown> = {},
) => {
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      const newKey = parentKey ? `${parentKey}.${key}` : key;
      const value = obj[key];

      // Nested property bags are flattened into dotted keys, except under `customProperties`,
      // whose entries are keyed by opaque form-field ids and are kept whole.
      if (isPlainRecord(value) && parentKey != "customProperties") {
        convertToDotNotation(value, newKey, result);
      } else {
        result[newKey] = value;
      }
    }
  }
  return result;
};

/**
 * Flattens the merged model-properties map `{ [fileVersionId]: { [nodeId]: node } }` to grid rows.
 * Callers hold that map under several node types (ModelPropertyNode, MergedNode), so the per-file
 * bucket is only `object` here and each node is read as an open record.
 */
export const getModifiedNodesList = (modelProperties: Record<string, object>): Record<string, unknown>[] => {
  // The per-file bucket is only `object`, so `Object.values` on it widens to `any[]`. Take the
  // nodes as `unknown` and re-check each one instead, so nothing untyped leaks into the rows.
  const nodes: unknown[] = Object.values(modelProperties).flatMap((bucket) => Object.values(bucket));
  return nodes.map((node, index: number) => {
    // A non-object node carries no identity fields, so it falls through to the index-based id below
    // rather than throwing — the shape it had before.
    const data: Record<string, unknown> = isRecord(node) ? node : {};
    return {
      // nodes without _id would all share id:undefined and break the DataView
      id: data.fileVersionId != null && data.nodeId != null ? `${data.fileVersionId}_${data.nodeId}` : `node_${index}`,
      ...convertToDotNotation(data),
    };
  });
};

// KNOWN BUG, preserved verbatim: unlike the SearchSet copy of this helper, this one has no
// null guard, so a rule with no field selected throws here.
export const extractPropertyPartialName = (field: string) => {
  const rootProperty = field.split(".");
  const value = rootProperty.slice(1).join(".").split("/");
  if (value.length > 1) {
    return value.slice(1).join("/");
  }
  // split() always yields at least one element.
  return value[0]!;
};
